import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ObjectProposalGenerationType } from '@econtractor/types';
import useSettingsParams from 'hooks/useSettingsParams';
import SvgTrash from 'components/svg/SvgTrash';
import { useObjectCard } from '../../context/ObjectCardProvider';
import SolarIcon from 'components/icons/SolarIcon';

export interface DocumentActionsProps {
    row: ObjectProposalGenerationType;
    deletingId: number | null;
    onCreateFromGeneration: (row: ObjectProposalGenerationType) => void;
    onToWarehouse: (row: ObjectProposalGenerationType) => void;
    onPayment: (row: ObjectProposalGenerationType) => void;
    onDelete: (row: ObjectProposalGenerationType) => void;
}

const DocumentActions = ({
    row,
    deletingId,
    onCreateFromGeneration,
    onToWarehouse,
    onPayment,
    onDelete,
}: DocumentActionsProps) => {
    const { t } = useTranslation(['Objects']);
    const params = useSettingsParams();
    const { isEditLocked } = useObjectCard();
    const canPayments = Boolean(params.objectPaymentControl);
    const isLeadProposal = (row.source || 'object') === 'lead';
    const isDeleted = Boolean(Number(row.deleted || 0));
    const isDeleting = deletingId === row.ID;
    const createTitle = t('documents.popup.create');
    const createHint = isLeadProposal
        ? t('documents.popup.createFromProposalHint')
        : t('documents.popup.createHint');

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean; disabled?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            disabled={options?.disabled}
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SolarIcon name="file-earmark-plus" />,
                createTitle,
                createHint,
                () => onCreateFromGeneration(row),
                { disabled: isDeleting || isEditLocked },
            )}

            {isLeadProposal ? (
                <>
                    <Dropdown.Divider className="my-2" />
                    {renderMenuItem(
                        <SolarIcon name="box-arrow-in-down" />,
                        t('documents.popup.toWarehouse'),
                        t('documents.popup.toWarehouseHint'),
                        () => onToWarehouse(row),
                        { disabled: isDeleted || isDeleting || isEditLocked },
                    )}
                </>
            ) : (
                <>
                    {canPayments ? (
                        <>
                            <Dropdown.Divider className="my-2" />
                            {renderMenuItem(
                                <SolarIcon name="cash-coin" />,
                                t('documents.popup.payment'),
                                t('documents.popup.paymentHint'),
                                () => onPayment(row),
                                { disabled: isDeleted || isDeleting || isEditLocked },
                            )}
                        </>
                    ) : null}
                    <Dropdown.Divider className="my-2" />
                    {renderMenuItem(
                        <SvgTrash />,
                        t('documents.popup.delete'),
                        t('documents.popup.deleteHint'),
                        () => onDelete(row),
                        { danger: true, disabled: isDeleted || isDeleting || isEditLocked },
                    )}
                </>
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-document-${row.source || 'object'}-${row.ID}`}
                disabled={isDeleting}
            >
                {isDeleting ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                    <SolarIcon name="three-dots" />
                )}
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default DocumentActions;
